/**
 * @file translation.js
 * @brief Provides the English translation catalogue for the frontend interface.
 */
const enTranslation = {
    language: {
        english: "EN",
        french: "FR",
        switchToEnglish: "Switch to English",
        switchToFrench: "Passer en francais",
    },
    common: {
        loading: "Loading...",
        saving: "Saving...",
        cancel: "Cancel",
        save: "Save",
        edit: "Edit",
        delete: "Delete",
        search: "Search...",
        noImage: "No image",
        unknown: "Unknown",
        pageOf: "Page {{page}} of {{total}}",
        previous: "Previous",
        next: "Next",
        clear: "Clear",
        clearAll: "Clear all",
        resultCount_one: "{{count}} result",
        resultCount_other: "{{count}} results",
        listingCount_one: "{{count}} listing found",
        listingCount_other: "{{count}} listings found",
        selectedCount_one: "{{count}} selected",
        selectedCount_other: "{{count}} selected",
    },
    app: {
        redirecting: "Redirecting...",
        authError: "Auth error",
        signInMessage: "Sign in to browse and sell items privately!",
        signIn: "Sign in with Keycloak",
        maintenanceTitle: "Under Maintenance",
        maintenanceMessage: "We're performing scheduled maintenance. Please check back soon.",
    },
    header: {
        sell: "Sell",
        moderation: "Moderation",
        catalog: "Catalog",
        settings: "Settings",
        favorites: "Favorites",
        messages: "Messages",
        profile: "Profile",
        logout: "Logout",
        closeMenu: "Close menu",
        openMenu: "Open menu",
    },
    listingList: {
        searchFavorites: "Search favorites...",
        searchListings: "Search listings...",
        clearSearch: "Clear search",
        toggleFilters: "Toggle filters",
        filters: "Filters",
        location: "Location",
        category: "Category",
        searchLabel: "Search",
        clearFilters: "Clear filters",
        error: "Something went wrong: {{message}}",
        noListings: "No listings found.",
        emptySummary: "Try adjusting or clearing your filters to broaden the results.",
        noDescription: "No description was provided for this listing.",
        addToFavorites: "Add to favorites",
        removeFromFavorites: "Remove from favorites",
        marketSectionTitle: "Listings",
        favoritesSectionTitle: "Saved listings",
        viewListing: "View listing",
    },
    listingForm: {
        loginRequired: "Please log in to create a listing.",
        title: "Create Listing",
        subtitle: "Fill in the details below to list your item.",
        titleField: "Title",
        titlePlaceholder: "e.g. Locomotive HO scale 1:87",
        descriptionField: "Description",
        descriptionPlaceholder: "Describe your item in detail...",
        priceField: "Price (EUR)",
        categoryField: "Category",
        locationField: "Location",
        selectCategory: "Select category",
        selectRootCategory: "Select a root category",
        selectSubCategory: "Select subcategory (level {{level}})",
        selectLocation: "Select location",
        loadingCategories: "Loading categories...",
        selectedCategoryPath: "Selected path: {{path}}",
        photosCount: "Photos ({{count}}/6)",
        dropHere: "Drop images here...",
        dropPrompt: "Drag and drop images, or click to browse",
        dropHint: "Max 6 images · JPG, PNG, WEBP",
        imageHint: "Drag to reorder · First image is the cover",
        removeImage: "Remove image",
        cover: "Cover",
        maxImagesAlert: "Maximum 6 images allowed",
        titleTooLong: "Title must be {{max}} characters or fewer.",
        descriptionTooLong: "Description must be {{max}} characters or fewer.",
        charactersCount: "{{count}} / {{max}} characters",
        saveDraft: "Save as Draft",
        submitting: "Submitting...",
        submitApproval: "Submit for Approval",
        publishDirectly: "Publish directly",
    },
    listingEdit: {
        title: "Edit Listing",
        subtitle: "Update your listing details below.",
        loading: "Loading listing...",
        notAllowed: "You are not allowed to edit this listing.",
        reject: "Reject",
        approve: "Approve",
    },
    listingDetails: {
        statuses: {
            draft: "Draft",
            pending: "Pending approval",
            active: "Active",
            transactionInProgress: "Transaction in progress",
            paused: "Paused",
            sold: "Sold",
            rejected: "Rejected",
        },
        actions: {
            submitReview: "Submit for review",
            pause: "Pause",
            resume: "Resume",
            markSold: "Mark as sold",
            approve: "Approve",
            reject: "Reject",
            delete: "Delete",
            back: "Back",
            manageListing: "Manage listing",
            contactAndOffer: "Contact seller or make an offer",
        },
        deleteConfirm: "Delete this listing?",
        loading: "Loading listing...",
        error: "Error loading listing",
        notFound: "Listing not found",
        clickToEnlarge: "Click to enlarge",
        byAuthor: "by {{name}}",
        removeFromFavorites: "Remove from favorites",
        addToFavorites: "Add to favorites",
        transactionLocked: "An active transaction is in progress. Editing is locked.",
    },
    contactSeller: {
        contact: "Contact Seller",
        messageTo: "Message to {{name}}",
        placeholder: "Hi, I'm interested in \"{{title}}\"...",
        buyNow: "Buy now ({{price}} EUR)",
        offerPlaceholder: "Your offer",
        sendOffer: "Send offer",
        offerInvalid: "Please enter a valid offer amount.",
        transactionInProgress: "A transaction is already in progress for this listing.",
        listingSold: "This listing has already been sold.",
        transactionCreateError: "Could not create transaction request.",
        sendError: "Failed to send message. Please try again.",
        sending: "Sending...",
        send: "Send Message",
    },
    messages: {
        title: "Messages",
        listingLabel: "Listing",
        search: "Search...",
        noResults: "No results.",
        noConversations: "No conversations yet.",
        selectConversation: "Select a conversation to start chatting",
        reconnecting: "Reconnecting...",
        loading: "Loading...",
        startConversation: "Start the conversation!",
        dropImages: "Drop images to attach",
        attachImages: "Attach images",
        writeMessage: "Write a message...",
        messageTooLong: "Message must be {{max}} characters or fewer.",
        charactersCount: "{{count}} / {{max}} characters",
        fallbackListing: "Listing #{{id}}",
        readDouble: "read",
        readSingle: "sent",
        transaction: {
            title: "Transaction",
            summary: "{{kind}} at {{offeredPrice}} EUR - {{status}}",
            accept: "Accept",
            refuse: "Refuse",
            confirmItemOk: "Confirm item is OK",
            actionError: "Could not process transaction action.",
            kinds: {
                buy: "Buy",
                offer: "Offer",
            },
            statuses: {
                pending: "Pending",
                accepted: "Accepted",
                refused: "Refused",
                completed: "Completed",
                cancelled: "Cancelled",
            },
        },
    },
    transactionErrors: {
        default: "Transaction request failed.",
        offerPriceRequired: "Offer amount is required.",
        offerPricePositive: "Offer amount must be greater than 0.",
        buyShouldNotIncludeOffer: "Buy request should not include a custom offer amount.",
        cannotBuyOwnListing: "You cannot start a transaction on your own listing.",
        listingUnavailable: "This listing is not available for transaction requests.",
        transactionAlreadyInProgress: "A transaction is already in progress for this listing.",
        onlySellerCanRespond: "Only the seller can respond to this transaction.",
        onlyPendingCanBeResponded: "Only pending transactions can be responded to.",
        transactionNoLongerPending: "This transaction is no longer pending.",
        listingNoLongerAvailableForAccept: "Listing is no longer available for accepting a transaction.",
        anotherTransactionAlreadyActive: "Another transaction is already active for this listing.",
        onlyBuyerCanConfirm: "Only the buyer can confirm transaction completion.",
        onlyAcceptedCanComplete: "Only accepted transactions can be completed.",
        transactionNoLongerActive: "This transaction is no longer active.",
        invalidDecision: "Invalid decision. Use accept or refuse.",
    },
    notifications: {
        unsupported: "Notifications are not supported in this browser.",
        httpsRequired: "Notifications require HTTPS (or localhost in development).",
        someone: "Someone",
        attachment: "Sent you an attachment",
        newMessageFrom: "New message from {{name}}",
        transactionTitle: "Transaction update from {{name}}",
        transactionBody: "Transaction status: {{status}}",
        transactionUpdated: "Updated",
        newModerationListingTitle: "New listing to moderate",
        newModerationListingBody: "\"{{title}}\" was submitted by {{author}}.",
        newModerationListingsTitle: "New listings to moderate",
        newModerationListingsBody: "{{count}} new listing(s) were added to moderation queue.",
        moderationListingFallbackTitle: "Untitled listing",
    },
    account: {
        loadError: "Failed to load profile",
        updateError: "Failed to update profile.",
        loading: "Loading profile...",
        title: "My Account",
        changeProfilePicture: "Change profile picture",
        changePhoto: "Change photo",
        bio: "Bio",
        bioPlaceholder: "Tell others a bit about yourself...",
        characters_one: "{{count}} character",
        characters_other: "{{count}} characters",
        updatedSuccess: "Profile updated successfully!",
        saveProfile: "Save Profile",
        myListings: "My Listings",
    },
    accountListings: {
        title: "My Listings",
        newListing: "New listing",
        searchPlaceholder: "Search my listings...",
        allStatuses: "All statuses",
        selectAll: "Select all",
        noListings: "No listings found.",
        pause: "Pause",
        resume: "Resume",
        archive: "Archive",
        unarchive: "Un-archive",
        deleteSelectedConfirm: "Delete selected listings?",
        deleteOneConfirm: "Delete this listing?",
        statuses: {
            draft: "Draft",
            pending: "Pending Approval",
            rejected: "Rejected",
            active: "Active",
            transactionInProgress: "Transaction in Progress",
            paused: "Paused",
            sold: "Sold",
            archived: "Archived",
        },
    },
    moderation: {
        title: "Moderation Panel",
        overview: {
            tab: "Overview",
            subtitle: "Quick pulse on listings, conversations, and users.",
            refresh: "Refresh overview",
            loadError: "Unable to load overview data.",
            pendingListings: "Pending listings",
            rejectedListings: "Rejected listings",
            activeListings: "Active listings",
            users: "Users",
            conversations: "Conversations",
            unreadMessages: "Unread messages",
            openQueue: "Open queue",
            reviewRejected: "Review rejected",
            browseUsers: "Browse users",
            openMessages: "Open messages",
            liveMarketplace: "Currently visible in marketplace",
            followUpNeeded: "Needs moderator follow-up",
            statusGraphTitle: "Listings status distribution",
            pressureGraphTitle: "Moderation pressure",
            temporalGraphTitle: "Active listings history",
            temporalGraphCreatedHint: "Last {{days}} days of newly created active listings.",
            temporalGraphSnapshotHint: "Estimated active total over {{days}} days (baseline + new active listings).",
            temporalGraphCreatedAria: "Temporal chart of newly created active listings",
            temporalGraphSnapshotAria: "Temporal chart of estimated total active listings",
            modeCreated: "New active/day",
            modeSnapshot: "Active total",
            pendingShare: "Pending share",
            pendingShareHint: "{{pending}} pending out of {{total}} tracked listings",
            pendingShareAria: "Pending listings share: {{percent}} percent",
            recentPending: "Recent pending listings",
            noPendingRecent: "No pending listings right now.",
            recentConversations: "Recent conversations",
            noConversations: "No conversations yet.",
            unreadCount_one: "{{count}} unread",
            unreadCount_other: "{{count}} unread",
        },
        pending: "Pending",
        rejected: "Rejected",
        users: "Users",
        searchUsers: "Search users...",
        searchListings: "Search listings...",
        approveAll: "Approve all",
        rejectAll: "Reject all",
        clearSelection: "Clear",
        selectAllOnPage: "Select all on page",
        noListings: "No listings found.",
        noUsers: "No users found.",
        approve: "Approve",
        reject: "Reject",
        reapprove: "Re-approve",
        view: "View",
        viewProfile: "View Profile",
        statuses: {
            pending: "Pending",
            active: "Active",
            rejected: "Rejected",
        },
    },
    catalogAdmin: {
        title: "Catalog Management",
        heroTitle: "Shape the marketplace taxonomy.",
        subtitle: "Browse from root categories, open branches on demand, and manage the catalog without preloading the whole tree.",
        tabAriaLabel: "Catalog sections",
        locationsTab: "Locations",
        locationsTabHint: "Cities and pickup areas",
        categoriesTab: "Categories",
        categoriesTabHint: "Nested category tree",
        locations: "Locations",
        categories: "Categories",
        locationsSubtitle: "All listing locations in one searchable list.",
        categoriesSubtitle: "Start from top-level categories, then fetch each branch only when you open it.",
        newLocationPlaceholder: "New location name...",
        namePlaceholder: "Name...",
        slugPlaceholder: "slug",
        searchLocations: "Search locations",
        searchAllCategories: "Search all categories",
        noParent: "No parent",
        noParentTopLevel: "No parent (top-level)",
        searchParentPlaceholder: "Search parent category (optional)",
        searchingParents: "Searching categories...",
        noParentResults: "No matching categories.",
        clearParent: "Clear parent",
        parentSelected: "Selected parent: {{name}}",
        add: "Add",
        addLocation: "Add location",
        addCategory: "Add category",
        adding: "Adding...",
        saving: "Saving...",
        save: "Save",
        cancel: "Cancel",
        edit: "Edit",
        delete: "Delete",
        refresh: "Refresh",
        topLevel: "Top-level",
        path: "Path",
        previous: "Previous",
        next: "Next",
        search: "Search",
        noLocations: "No locations yet.",
        noCategories: "No categories yet.",
        loadFailed: "Failed to load catalog data.",
        loadChildrenFailed: "Failed to load sub-categories.",
        addLocationFailed: "Failed to add location.",
        updateFailed: "Failed to update.",
        deleteFailed: "Failed to delete.",
        addCategoryFailed: "Failed to add category.",
        locationNameTooLong: "Location name must be {{max}} characters or fewer.",
        categoryNameTooLong: "Category name must be {{max}} characters or fewer.",
        categorySlugTooLong: "Category slug must be {{max}} characters or fewer.",
        deleteLocationConfirm: "Delete this location? Listings using it will lose their location.",
        deleteCategoryConfirm: "Delete this category? Sub-categories will also be removed.",
        totalLocations: "Locations",
        topLevelCategories: "Top-level",
        loadedBranches: "Loaded items",
        openBranches: "Open branches",
        visibleRoots: "Visible roots",
        childCount_one: "{{count}} child",
        childCount_other: "{{count}} children",
        loadMoreChildren: "Load more children",
        loadingChildren: "Loading children...",
        resultCount_one: "{{count}} result",
        resultCount_other: "{{count}} results",
    },
    localAuth: {
        login: "Log in",
        register: "Register",
        username: "Username",
        email: "Email (optional)",
        password: "Password",
        or: "or",
        loginFailed: "Login failed. Please check your credentials.",
        registerFailed: "Registration failed. Please try again.",
    },
    siteSettings: {
        eyebrow: "Administration",
        title: "Site Settings",
        subtitle: "Manage platform-wide configuration. Changes apply immediately to all users.",
        loadFailed: "Failed to load settings.",
        saveFailed: "Failed to save settings.",
        saved: "Settings saved!",
        generalSection: "General",
        generalSectionHint: "Core platform identity and availability.",
        authSection: "Authentication",
        authSectionHint: "Choose which login methods are available to users.",
        authSsoEnabled: "Single Sign-On (SSO)",
        authSsoEnabledHint: "Allow users to log in via the configured OIDC provider.",
        authLocalEnabled: "Local accounts",
        authLocalEnabledHint: "Allow users to register and log in with a username and password.",
        listingsSection: "Listings",
        listingsSectionHint: "Control listing limits and visibility for your marketplace.",
        siteName: "Site name",
        siteNameHint: "Displayed in the header and browser title.",
        motd: "Message of the day",
        motdHint: "Shown as a banner to all users. Leave empty to hide.",
        maxListingImages: "Max images per listing",
        maxListingsPerUser: "Max listings per user",
        listingsPublic: "Public listings",
        listingsPublicHint: "When enabled, active listings are visible to visitors without signing in.",
        maintenanceMode: "Maintenance mode",
        maintenanceModeHint: "When enabled, non-staff users see a maintenance page.",
        ssoSection: "Single Sign-On (SSO)",
        ssoSectionHint: "Override the default OIDC provider. Leave fields empty to use the build-time defaults. Changes take effect on next page load.",
        oidcAuthority: "OIDC Authority URL",
        oidcAuthorityHint: "The issuer URL of the OpenID Connect provider (e.g. https://login.example.com/realms/myrealm).",
        oidcClientId: "OIDC Client ID",
        oidcClientIdHint: "The client identifier registered with the OIDC provider.",
        oidcAlgorithm: "JWT Signing Algorithm",
        oidcAlgorithmHint: "The algorithm used by the OIDC provider to sign tokens (must match the provider's configuration).",
        ssoReloadWarning: "Saving SSO changes will reload the page and require you to sign in again.",
    },
    userProfile: {
        loginRequired: "Please log in.",
        loading: "Loading profile...",
        error: "Error: {{message}}",
        activeListings_one: "{{count}} active listing",
        activeListings_other: "{{count}} active listings",
        editProfile: "Edit profile",
        avatarPreview: "avatar preview",
        saveChanges: "Save changes",
        noActiveListings: "This user has no active listings.",
    },
};

export default enTranslation;
